import { axiosWithToken } from 'libs/configureAxios'
import { BasicResponse } from 'types/conmon'
import {
  CacheStatisticsResponse,
  CompareSummaryCacheRequest,
  CompareSummaryCachePreviewResponse,
  PreviewResponse,
  RefreshCacheRequest,
  RefreshCacheResponse,
  TaskStatusResponse,
} from './cacheManagementServices.type'

/**
 * Cache Management Services
 */
export class CacheManagementServices {
  /**
   * Get cache statistics
   */
  async getStatistics(): Promise<BasicResponse<CacheStatisticsResponse>> {
    return (await axiosWithToken.get(`/v1/admin/cache/statistics`)).data
  }

  /**
   * Preview summary cache refresh
   */
  async previewSummaryCache(params: {
    before_date?: string
    news_ids?: string
    filter_top_400_companies?: boolean
  }): Promise<BasicResponse<PreviewResponse>> {
    return (
      await axiosWithToken.get(
        `/v1/admin/cache/preview/summary-cache/refresh`,
        { params },
      )
    ).data
  }

  /**
   * Refresh summary cache
   */
  async refreshSummaryCache(
    body: RefreshCacheRequest,
  ): Promise<BasicResponse<RefreshCacheResponse>> {
    return (
      await axiosWithToken.post(`/v1/admin/cache/summary-cache/refresh`, body)
    ).data
  }

  /**
   * Preview wordcloud cache refresh
   */
  async previewWordCloudCache(params: {
    before_date?: string
    news_ids?: string
    filter_top_400_companies?: boolean
    /** Comma-separated ngram values, e.g. "1,2,3". Omit = ngram 2 (legacy) */
    ngrams?: string
  }): Promise<BasicResponse<PreviewResponse>> {
    return (
      await axiosWithToken.get(
        `/v1/admin/cache/preview/wordcloud-cache/refresh`,
        { params },
      )
    ).data
  }

  /**
   * Refresh wordcloud cache
   */
  async refreshWordCloudCache(
    body: RefreshCacheRequest,
  ): Promise<BasicResponse<RefreshCacheResponse>> {
    return (
      await axiosWithToken.post(`/v1/admin/cache/wordcloud-cache/refresh`, body)
    ).data
  }

  /**
   * Generate Wordcloud Analyze Results
   */
  async generateWordCloudAnalyze(): Promise<
    BasicResponse<RefreshCacheResponse>
  > {
    return (
      await axiosWithToken.post(`/v1/admin/cache/wordcloud-analyze/generate`)
    ).data
  }

  /**
   * Preview compare summary cache (check if cache exists for given ordered news_ids)
   */
  async previewCompareSummaryCache(
    body: CompareSummaryCacheRequest,
  ): Promise<BasicResponse<CompareSummaryCachePreviewResponse>> {
    return (
      await axiosWithToken.post(
        `/v1/admin/cache/compare-summary-cache/preview`,
        body,
      )
    ).data
  }

  /**
   * Refresh compare summary cache for the given ordered news_ids (clear + regenerate)
   */
  async refreshCompareSummaryCache(
    body: CompareSummaryCacheRequest,
  ): Promise<BasicResponse<RefreshCacheResponse>> {
    return (
      await axiosWithToken.post(
        `/v1/admin/cache/compare-summary-cache/refresh`,
        body,
      )
    ).data
  }

  /**
   * Get task status
   */
  async getTaskStatus(
    taskId: string,
  ): Promise<BasicResponse<TaskStatusResponse>> {
    return (await axiosWithToken.get(`/v1/admin/cache/task-status/${taskId}`))
      .data
  }

  /**
   * Preview abbreviation cache refresh
   */
  async previewAbbreviationCache(params: {
    before_date?: string
    news_ids?: string
    filter_top_400_companies?: boolean
  }): Promise<BasicResponse<PreviewResponse>> {
    return (
      await axiosWithToken.get(
        `/v1/admin/cache/preview/abbreviation-cache/refresh`,
        { params },
      )
    ).data
  }

  /**
   * Refresh abbreviation cache (re-generate abbreviation_300)
   */
  async refreshAbbreviationCache(
    body: RefreshCacheRequest,
  ): Promise<BasicResponse<RefreshCacheResponse>> {
    return (
      await axiosWithToken.post(
        `/v1/admin/cache/abbreviation-cache/refresh`,
        body,
      )
    ).data
  }

  /**
   * Preview sentiment cache refresh
   */
  async previewSentimentCache(params: {
    before_date?: string
    news_ids?: string
    filter_top_400_companies?: boolean
  }): Promise<BasicResponse<PreviewResponse>> {
    return (
      await axiosWithToken.get(
        `/v1/admin/cache/preview/sentiment-cache/refresh`,
        { params },
      )
    ).data
  }

  /**
   * Refresh sentiment cache (re-run sentiment analysis)
   */
  async refreshSentimentCache(
    body: RefreshCacheRequest,
  ): Promise<BasicResponse<RefreshCacheResponse>> {
    return (
      await axiosWithToken.post(`/v1/admin/cache/sentiment-cache/refresh`, body)
    ).data
  }
}
